import * as androidPay from './androidPay/androidPay';
import * as applePay from './applePay/applePay';
import * as paypalOneTouch from './paypalOneTouch/paypalOneTouch';
import * as venmo from './venmo/venmo';
import * as creditCard from './creditCard/creditCard';
import { PaymentError } from '../lib/error';
import { init as initBraintree } from '../lib/wallets';
import logger from '../lib/log';

const log = logger('Payment Methods');

let activePaymentMethodId;
let availableMethods = []; // Details about each of the payment methods that can be used externally - e.g. name, id, logo, etc.
let enabledPaymentModules = []; // Dynamic access module functions based on provided payment method id
let initialized = false;
let initOptions = {};

export const membershipPaymentMethods = ['creditcard'];

/**
 * Identifies if there are any applicable restrictions on the selected payment method.
 */
export const hasRestrictions = () => enabledPaymentModules.length > availableMethods.length;

/**
 * Most of the time, this simply returns the enabled payment methods. However, if there are any
 * restrictions on the payment methods (likely as a result of a promotion), this will only return
 * payment methods that can be used.
 *
 * @returns {Object[]} methods
 *          {String}   methods[].id - The id specific to the payment method
 *          {String}   methods[].name - The display name for the payment method
 *          {Boolean}  methods[].isCreditCard - Flag indicating if this is the credit card method (i.e. not a digital wallet)
 *          {String}   methods[].icon - The filename for the payment method icon
 *          {Object}   methods[].iconSet - The filenames if the payment method has multiple icons
 */
export const getAvailableMethods = () => availableMethods;

/**
 * Returns the active method that has been selected (and is *currently* available).
 */
export const getActiveMethod = () => _.find(availableMethods, ['id', activePaymentMethodId]);

/**
 * Indicates if there is a selected active method that is currently available.
 */
export const hasActiveMethod = () => !!getActiveMethod();

/**
 * Returns whether or not the selected payment method has been verified or not
 */
export const isActiveMethodAuthorized = () => {
  if (!hasActiveMethod()) return false;

  const activeMethod = _.find(enabledPaymentModules, ['PAYMENT_METHOD_ID', activePaymentMethodId]);
  return activeMethod && activeMethod.isAuthorized();
};

/**
 * Returns the payment method module that corresponds to the provided id.
 * The module will export the standard properties and functions that all wallets use:
 *   `select`, `authorize`, `PAYMENT_METHOD_ID`, `PAYMENT_METHOD_NAME`, etc.
 */
const getWallet = (walletId) => {
  const wallet = _.find(enabledPaymentModules, ['PAYMENT_METHOD_ID', walletId]) || {};
  return wallet.PAYMENT_METHOD_ID !== creditCard.PAYMENT_METHOD_ID ? wallet : null;
};

/**
 * Prepares the specified wallets with safeguards to ensure that the wallets are both enabled and available before
 * continuing with the selections.
 *
 * NOTE: This is for WALLETS only.
 * Credit card payment methods have their own flow and should not follow the same pattern as the other wallets.
 *
 * @async
 * @param {Object} options
 * @param {String[]} options.walletIds - the ids of the payment methods to prepare
 * @param {function=} options.success - The callback function used when the preparation is successful
 */
export const prepareWallets = async ({ walletIds, success }) => {
  log('prepareWallets', walletIds);

  const preparedWallets = await Promise.all(walletIds.map((walletId) => {
    try {
      const wallet = getWallet(walletId);
      if (!wallet) throw new PaymentError();
      return wallet.prepare();
    } catch (err) {
      log('Preparation failed', { err, walletId });
      return Promise.resolve();
    }
  }));

  if (success) {
    success(preparedWallets);
  }
};

/**
 * Selects the specified wallet as the active payment method. This adds safegaurds to ensure that the wallet is both enabled and available before
 * continuing with the selections.
 *
 * NOTE: This is for WALLETS only -- Credit card payment methods have their own flow and should not follow the same pattern as the other wallets.
 *
 * @async
 * @param {Object} options
 * @param {String} options.walletId - the id of the payment method to activate
 * @param {function=} options.success - The callback function used when the activation is successful
 * @param {function=} options.error - The callback function when the activation fails
 * @param {function=} options.autoAuthorizeError - The callback function used when authorization failed within selection
 * @param {function=} options.autoAuthorizeSuccess - The callback function used when payment authorized within selection
 */
export const selectWallet = async ({
  walletId,
  success,
  error,
  autoAuthorizeError,
  autoAuthorizeSuccess,
}) => {
  log('selectWallet', walletId);
  const wallet = getWallet(walletId);
  try {
    if (!wallet) throw new PaymentError();

    await wallet.select({ autoAuthorizeError, autoAuthorizeSuccess });
    activePaymentMethodId = walletId;

    if (success) success(wallet.PAYMENT_METHOD_NAME);
  } catch (err) {
    log('Selection failed', { err, walletId });
    const walletName = _.get(wallet, 'PAYMENT_METHOD_NAME');
    if (error) error(new PaymentError(err), walletName);
  }
};

/**
 * authorizeWallet
 *
 * Performs the appropriate authorization to verify the payment method for the transaction.
 *
 * NOTE: This is ONLY for wallets and will NOT authorize credit cards.
 *
 * @async
 * @param {Object} options
 * @param {String} options.walletId - The id of the wallet to be verified
 * @param {*} options.purchaseTotal -
 * @param {String} options.displayName -
 * @param {boolean=} options.buyNow
 * @param {function} options.success - The callback function used when the verification succeeds
 * @param {function} options.error - The callback function when the verification fails
 */
export const authorizeWallet = async ({
  walletId,
  purchaseTotal,
  displayName,
  buyNow,
  success,
  error,
  cancel,
}) => {
  log('authorizeWallet', {
    walletId,
    purchaseTotal,
    displayName,
  });

  const wallet = getWallet(walletId);
  try {
    if (!wallet) throw new PaymentError();

    await wallet.authorize({
      purchaseTotal,
      displayName,
      buyNow,
    });

    if (!wallet.isAuthorized()) {
      if (cancel) cancel();
      return;
    }

    if (success) {
      success({
        autoCompletePurchase: wallet.AUTO_COMPLETE_PURCHASE,
      });
    }
  } catch (err) {
    log('Authorization Failed', { err, walletId });
    const walletName = _.get(wallet, 'PAYMENT_METHOD_NAME');
    if (error) error(new PaymentError(err), walletName);
  }
};

/**
 * activateCreditCard
 *
 * Used to set the EXISTING card on file as the active payment on page load and user login.
 * This will do nothing if there already is an active payment method as it is only meant to be
 * a "default" selection if the user has not already explicitly selected another method.
 *
 * NOTE: This should ONLY be called when there actually is a tokenized card on file that is
 * ready for use (and not expired).
 */
export const activateCreditCard = () => {
  if (!hasActiveMethod()) {
    creditCard.activate();
    activePaymentMethodId = creditCard.PAYMENT_METHOD_ID;
    log('Credit card is now the active payment method');
  } else {
    log('SKIPPING activateCreditCard', getActiveMethod());
  }
};

/**
 * submitCreditCard
 *
 * Adds (or updates) the credit card to use for the transaction and then sets it as the active payment method.
 * NOTE: Client-side validation should be executed *before* calling this.
 *
 * @async
 * @param {Object} options
 * @param {String} options.cardNumber - Credit card number (can potentially be a masked number)
 * @param {String} options.cvv - The security number for the card
 * @param {String} options.expirationMonth - The card's expiration month in the format of MM
 * @param {String} options.expirationYear - The card's expiration year in the format of YY
 * @param {String} options.zipCode - The billing postal code
 * @param {Boolean=} options.saveCard - Flag to indicate if the card should be saved for future transactions
 * @param {function} options.success - The callback function used when the verification succeeds
 * @param {function} options.error - The callback function when the verification fails
 */
export const submitCreditCard = async ({ success, error, ...rest }) => {
  try {
    const response = await creditCard.submit(rest);
    activePaymentMethodId = creditCard.PAYMENT_METHOD_ID;
    if (success) success(response);
  } catch (err) {
    if (error) error(new PaymentError(err));
  }
};

/**
 * Some promotions will restrict the payment methods that are available to the user.
 * This function updates the various payment methods to identify if they have are restricted
 * or not.
 *
 * NOTE: If a list of eligible methods is not explicitly provided - then no restrictions are applied.
 *
 * @param {Array.<string>=} eligibleMethodIds - List of method ids that the user is restricted to use
 */
export const updateAvailableMethods = (eligibleMethodIds) => {
  const eligibleMethods = _.filter(enabledPaymentModules,
    ({ PAYMENT_METHOD_ID }) => _.includes(eligibleMethodIds, PAYMENT_METHOD_ID));

  // Do not enforce the restrictions if the only `eligible` methods are not enabled
  availableMethods = _.map(
    _.isEmpty(eligibleMethods) ? enabledPaymentModules : eligibleMethods,
    (paymentMethod) => ({
      id: paymentMethod.PAYMENT_METHOD_ID,
      name: paymentMethod.PAYMENT_METHOD_NAME,
      icon: paymentMethod.ICON_FILENAME,
      iconSet: paymentMethod.ICON_SET,
      isCreditCard: paymentMethod.PAYMENT_METHOD_ID === creditCard.PAYMENT_METHOD_ID,
      autoCompletePurchase: paymentMethod.AUTO_COMPLETE_PURCHASE,
    }),
  );

  const { isOptIn, isSoftOptIn } = Commerce.state.membership;

  if (isOptIn || isSoftOptIn) {
    availableMethods = availableMethods.filter(({ id }) => membershipPaymentMethods.includes(id));
  }

  log('Payment Method Restrictions Updated', { eligibleMethodIds, availableMethods });
};

/**
 * Gets the id for the payment method that should be the default method. This
 * takes into account the user's last payment method, whether or not they have
 * a credit card on file, what payment methods are available, etc.
 */
const getInitialMethodId = ({ hasCreditCard, lastPaymentMethodId, primaryPaymentMethodId }) => {
  if (availableMethods.length === 1) {
    // There is only one available payment method
    log('getInitialMethodId - there is only one available payment method', availableMethods[0].id);
    return availableMethods[0].id;
  }

  if (primaryPaymentMethodId && availableMethods.some(({ id }) => id === primaryPaymentMethodId)) {
    log('getInitialMethodId - using primaryPaymentMethod', primaryPaymentMethodId);
    return primaryPaymentMethodId;
  }

  if (lastPaymentMethodId && availableMethods.some(({ id }) => id === lastPaymentMethodId)) {
    log('getInitialMethodId - using cookied last payment method', lastPaymentMethodId);
    return lastPaymentMethodId;
  }

  log('getInitialMethodId - hasCreditCard?', hasCreditCard);
  return hasCreditCard ? creditCard.PAYMENT_METHOD_ID : null;
};

/**
 * Initializes the various payment methods available and potentially sets
 * an active payment method. This *only* deals with data and does not directly
 * affect the DOM. This is intented to run BEFORE we render our components to the
 * page so that we can render the available methods right away as opposed to
 * showing/hiding all options and then adjusting the visiblity after we initialize
 * the methods.
 */
export const init = async ({
  complete, ...options
}) => {
  const {
    autoAuthorizeError,
    autoAuthorizeSuccess,
    enabledWalletIds,
    eligibleMethodIds,
    hasCreditCard,
    lastPaymentMethodId,
    isAppWebView,
    isIosApp,
    primaryPaymentMethodId,
    reducedUIFeaturesEnabled,
  } = options;
  initOptions = options;
  creditCard.init({ isAppWebView, isIosApp });
  enabledPaymentModules = [creditCard];

  await initBraintree();
  const wallets = reducedUIFeaturesEnabled ? [] : [paypalOneTouch, applePay, androidPay, venmo];

  await Promise.all(wallets.map(async (wallet) => {
    if (!_.includes(enabledWalletIds, wallet.PAYMENT_METHOD_ID)) return;
    const isEnabled = await wallet.init(options);
    if (isEnabled) enabledPaymentModules.push(wallet);
  }));

  log('init - initialized payment methods:', _.map(enabledPaymentModules, 'PAYMENT_METHOD_ID'));

  updateAvailableMethods(eligibleMethodIds);

  const paymentMethodId = getInitialMethodId({ hasCreditCard, lastPaymentMethodId, primaryPaymentMethodId });
  log('init - initial payment method id:', paymentMethodId);
  if (paymentMethodId) {
    if (paymentMethodId === creditCard.PAYMENT_METHOD_ID && hasCreditCard) {
      activateCreditCard();
    } else {
      await selectWallet({
        walletId: paymentMethodId,
        autoAuthorizeError,
        autoAuthorizeSuccess,
      }).catch(() => {});
    }
  }

  log('init - active payment method:', getActiveMethod());
  initialized = true;
  if (complete) complete();
};

/**
 * Inidicates whether or not the initialization is complete - allowing components to distinguish
 * no available methods from ones that haven't finished initializing yet
 * */
export const isInitializationComplete = () => initialized;

/**  */
export const debugPaymentMethods = () => {
  const available = [];
  const unavailable = [];
  const methods = [creditCard, paypalOneTouch, applePay, androidPay, venmo];
  methods.forEach(({ PAYMENT_METHOD_ID: id, PAYMENT_METHOD_NAME: name }) => {
    if (_.find(availableMethods, ['id', id])) {
      available.push(name);
    } else if (id !== creditCard.PAYMENT_METHOD_ID && !_.includes(initOptions.enabledWalletIds, id)) {
      unavailable.push(`${name} - Disabled by server`);
    } else if (!_.find(enabledPaymentModules, ['PAYMENT_METHOD_ID', id])) {
      unavailable.push(`${name} - Disabled by client initialization`);
    } else {
      unavailable.push(`${name} - Disabled by restrictions`);
    }
  });

  if (!window.localStorage || !_.includes(localStorage.debug, 'Payments')) {
    window.DEBUG_PAYMENTS = true;
  }
  log('Debug Payment Methods - Available', available);
  log('Debug Payment Methods - Unavailable', unavailable);
  log('Debug Payment Methods - Details', {
    initOptions,
    enabledMethods: _.map(enabledPaymentModules, 'PAYMENT_METHOD_NAME'),
    hasRestrictions: hasRestrictions(),
    availableMethods: _.map(getAvailableMethods(), 'name'),
    hasActiveMethod: hasActiveMethod(),
    getActiveMethod: _.get(getActiveMethod(), 'name'),
  });
};

export default init;
